/* eslint-disable react/react-in-jsx-scope */
import { getSubdomain } from '@/utils/getSubdomain';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Button, HStack } from '@chakra-ui/react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { BiShare } from 'react-icons/bi';
import { FaShare } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import { TableCell } from './components/TableCells';
import { ITemplateTasksListType } from './types';

export const templateTasksIndexKey = 'template_tasks';

const useColumnNames = () => {
  const { t } = useTranslation();
  return {
    name: t('common.name'),
    memberName: t('registrations.member'),
    localName: t('registrations.location'),
    dueIn: t('registrations.sla'),
    formularyName: t('registrations.checklist'),
    createdAt: t('registrations.created_at'),
    updatedAt: t('registrations.updated_at'),
    author: t('registrations.created_by'),
  } as Partial<Record<keyof ITemplateTasksListType, string>>;
};

const columnHelper = createColumnHelper<ITemplateTasksListType>();

type GetTableColumnsParams = {
  onDelete?: (id: string) => void;
  onView?: (id: string) => void;
  onShare?: (id: string) => void;
};

export const getTableColumns = (props?: GetTableColumnsParams) => {
  const columnNames = useColumnNames();
  const { t } = useTranslation();
  const tableCols: ColumnDef<ITemplateTasksListType, any>[] = [
    columnHelper.accessor('name', {
      header: columnNames.name,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="default" content={cell.getValue()} />
        ) : (
          t('registrations.no_name')
        ),
    }),
    columnHelper.accessor('formularyName', {
      header: columnNames.formularyName,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="default" content={cell.getValue()} />
        ) : (
          t('registrations.no_checklist')
        ),
    }),
    columnHelper.accessor('memberName', {
      header: columnNames.memberName,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="user" content={cell.getValue()} />
        ) : (
          t('registrations.no_member')
        ),
    }),
    columnHelper.accessor('localName', {
      header: columnNames.localName,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell
            type="local"
            content={cell.getValue()}
            address={cell.row.original.localAddressName}
          />
        ) : (
          t('registrations.no_location')
        ),
    }),
    columnHelper.accessor('dueIn', {
      header: columnNames.dueIn,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell
            type="default"
            content={`${moment.duration(cell.getValue()).humanize()}`}
          />
        ) : (
          'Sem SLA'
        ),
    }),
    columnHelper.accessor('createdAt', {
      header: columnNames.createdAt,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="date" content={new Date(cell.getValue())} />
        ) : (
          '...'
        ),
    }),
    columnHelper.accessor('updatedAt', {
      header: columnNames.updatedAt,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="date" content={new Date(cell.getValue())} />
        ) : (
          '...'
        ),
    }),
    columnHelper.accessor('author', {
      header: columnNames.author,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <TableCell type="user" content={cell.getValue()} />
        ) : (
          '...'
        ),
    }),
    columnHelper.display({
      id: 'actions',
      size: 0,
      cell: ({ row }) => (
        <HStack>
          <Button
            colorScheme="brand"
            onClick={() => props?.onView?.(row.original.id)}
          >
            {t('common.view')}
          </Button>

          <Button
            variant="outline"
            rightIcon={<ExternalLinkIcon />}
            onClick={() => props?.onShare?.(row.original.id)}
          >
            {t('registrations.share')}
          </Button>
          <Button
            colorScheme="red"
            onClick={() => props?.onDelete?.(row.original.id)}
          >
            {t('common.delete')}
          </Button>
        </HStack>
      ),
    }),
  ];
  return tableCols;
};

export const downloadQrCode = (id: string) => {
  const svg = document.getElementById('qrcode');
  if (!svg) throw new Error('No svg!');
  const svgData = new XMLSerializer().serializeToString(svg!);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();
  img.onload = function () {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx!.drawImage(img, 0, 0);
    const pngFile = canvas.toDataURL('image/png');

    const downloadLink = document.createElement('a');
    downloadLink.download = `qrcode-${id}`;
    downloadLink.href = `${pngFile}`;
    downloadLink.click();
  };

  img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
};

export const getShareableLink = (id: string, auth?: boolean) => {
  const { protocol, host } = window.location;
  const baseUrl = `${protocol}//${host}`;
  const path = auth ? `/app/task/${id}` : `/external/task/${id}`;
  const url = new URL(path, baseUrl);
  return url;
};
